import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';

export interface CustomActivityType {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  icon: string;
  active: boolean;
}

// Tipi di sistema mostrati nei form (subset UI dei valori DB: meeting/note/alfadocs_* non si creano da form)
export const SYSTEM_ACTIVITY_TYPES: { value: string; label: string }[] = [
  { value: 'call', label: 'Chiamata' },
  { value: 'videochiamata', label: 'Videochiamata' },
  { value: 'appuntamento', label: 'Appuntamento fisico' },
  { value: 'sopralluogo', label: 'Sopralluogo' },
  { value: 'email', label: 'Email' },
  { value: 'task', label: 'Task' },
  { value: 'other', label: 'Altro' },
];

// Valori riservati: quelli ammessi dal trigger DB (validate_sales_activity_type)
// + 'meeting' (legacy: righe storiche e mapping default meeting→videochiamata in ActivitySettings)
const SYSTEM_SLUGS = [
  'call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other',
  'alfadocs_visita', 'alfadocs_operazione', 'alfadocs_telehealth',
  'meeting',
];

export const CUSTOM_TYPE_ICONS = [
  'clipboard-list', 'stethoscope', 'heart-pulse', 'bell', 'repeat',
  'star', 'calendar-check', 'message-circle', 'wrench', 'users',
] as const;

// Genera sempre uno slug non-vuoto conforme al CHECK DB (slug ~ '^[a-z0-9_]+$').
// Se il nome non contiene alcun carattere [a-z0-9] utile (es. solo emoji/punteggiatura)
// o collide con uno slug di sistema riservato, ripiega su un suffisso "custom" univoco.
function slugifyTypeName(name: string): string {
  const base = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!base) {
    // Fallback: nessun carattere valido nel nome originale (es. solo emoji/spazi/punteggiatura).
    // Genera comunque uno slug valido e verosimilmente univoco per l'org.
    return `custom_${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
  }

  return SYSTEM_SLUGS.includes(base) ? `${base}_custom` : base;
}

export function getActivityTypeLabel(type: string, customTypes: CustomActivityType[]): string {
  const system = SYSTEM_ACTIVITY_TYPES.find(t => t.value === type);
  if (system) return system.label;
  const custom = customTypes.find(t => t.slug === type);
  return custom ? custom.name : type;
}

interface ActivityTypesStore {
  customTypes: CustomActivityType[];
  loading: boolean;
  fetched: boolean;
  fetchCustomTypes: () => Promise<void>;
  createCustomType: (input: { name: string; icon: string }) => Promise<CustomActivityType | null>;
  updateCustomType: (id: string, patch: Partial<Pick<CustomActivityType, 'name' | 'icon'>>) => Promise<void>;
  setActive: (id: string, active: boolean) => Promise<void>;
}

// Promise dello scaricamento in corso, condivisa tra chiamate concorrenti: senza questa,
// un `await fetchCustomTypes()` lanciato da un handleSubmit che parte DOPO il fetch di
// mount (già `loading`) tornava subito (early-return sincrono su `loading`), lasciando
// `customTypes` ancora vuoto nonostante l'`await` — la fetch di mount restava non attesa.
let inFlightFetch: Promise<void> | null = null;

export const useActivityTypesStore = create<ActivityTypesStore>((set, get) => ({
  customTypes: [],
  loading: false,
  fetched: false,

  fetchCustomTypes: async () => {
    if (get().fetched) return;
    if (inFlightFetch) return inFlightFetch;
    set({ loading: true });
    inFlightFetch = (async () => {
      try {
        // RLS limita già all'org dell'utente
        const { data, error } = await supabase
          .from('sales_activity_types')
          .select('id, organization_id, slug, name, icon, active')
          .order('name');
        if (error) throw error;
        set({ customTypes: data || [], fetched: true });
      } catch (err) {
        console.error('[activityTypesStore] Errore caricamento tipi attività custom', err);
      } finally {
        set({ loading: false });
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  createCustomType: async ({ name, icon }) => {
    const orgId = await getCurrentUserOrganizationId();
    if (!orgId) return null;
    const slug = slugifyTypeName(name);
    if (!slug) return null;
    const { data, error } = await supabase
      .from('sales_activity_types')
      .insert({ organization_id: orgId, slug, name: name.trim(), icon })
      .select('id, organization_id, slug, name, icon, active')
      .single();
    if (error) {
      console.error('[activityTypesStore] Errore creazione tipo attività custom', error);
      if (error.code === '23505') {
        throw new Error('Esiste già un tipo attività con questo nome');
      }
      throw error;
    }
    set({ customTypes: [...get().customTypes, data].sort((a, b) => a.name.localeCompare(b.name)) });
    return data;
  },

  updateCustomType: async (id, patch) => {
    const { error } = await supabase
      .from('sales_activity_types')
      .update(patch)
      .eq('id', id);
    if (error) {
      console.error('[activityTypesStore] Errore aggiornamento tipo attività custom', error);
      throw error;
    }
    set({ customTypes: get().customTypes.map(t => (t.id === id ? { ...t, ...patch } : t)) });
  },

  setActive: async (id, active) => {
    const { error } = await supabase
      .from('sales_activity_types')
      .update({ active })
      .eq('id', id);
    if (error) {
      console.error('[activityTypesStore] Errore attivazione/disattivazione tipo attività custom', error);
      throw error;
    }
    set({ customTypes: get().customTypes.map(t => (t.id === id ? { ...t, active } : t)) });
  },
}));
